/**
 * @fileoverview Hook for managing column visibility state with localStorage persistence.
 */

import { useState, useCallback, useMemo } from 'react';
import type { ColumnDefinition } from '../types/ColumnConfig';

interface UseColumnVisibilityOptions {
  /** Column definitions to derive defaults from */
  columns: ColumnDefinition[];
  /** localStorage key for persisting visibility state */
  storageKey: string;
}

/**
 * Manages column visibility state.
 *
 * - Initializes from localStorage, falling back to `hiddenByDefault` flags on columns.
 * - Persists changes to localStorage.
 * - Returns hiddenColumns Set and a setter.
 */
export function useColumnVisibility({ columns, storageKey }: UseColumnVisibilityOptions) {
  const defaultHidden = useMemo(() => {
    return new Set(
      columns
        .filter((col) => col.hiddenByDefault)
        .map((col) => col.id)
    );
  }, [columns]);

  const [hiddenColumns, setHiddenColumnsRaw] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set(parsed);
        }
      }
    } catch {
      // ignore
    }
    return defaultHidden;
  });

  const setHiddenColumns = useCallback(
    (next: Set<string>) => {
      setHiddenColumnsRaw(next);
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        // ignore
      }
    },
    [storageKey]
  );

  return { hiddenColumns, setHiddenColumns };
}
